// eslint-disable-next-line no-unused-vars
import React from 'react';
import styled from '@emotion/styled';
import Carousel from 'react-multi-carousel';
import { arrayOf, bool, func, number, shape, string } from 'prop-types';

const StyledSlider = styled.section`
  box-sizing: border-box;
  position: relative;
  width: 100%;

  &.withDots {
    padding-bottom: 2rem;
  }

  .sliderCarousel {
    margin-inline: calc(var(--slider-gap) / -2);
    width: calc(100% + var(--slider-gap));
  }

  .sliderItem {
    align-items: stretch;
    padding-inline: calc(var(--slider-gap) / 2);
  }

  .sliderSlide {
    min-width: 0;
    width: 100%;
  }

  .sliderDots {
    bottom: 0;
  }

  .react-multiple-carousel__arrow {
    z-index: 5;
  }
`;

const itemCount = value => Math.max(1, Math.floor(value || 1));

const SliderComponent = ({
  slides,
  editingSlide,
  desktopItems,
  tabletItems,
  mobileItems,
  gap,
  showArrows,
  showDots,
  autoPlay,
  autoPlaySpeed,
  puck,
}) => {
  const isEditing = Boolean(puck?.isEditing);

  if (!slides?.length) return null;

  if (isEditing) {
    const slideIndex = Math.min(
      Math.max(1, Math.floor(editingSlide || 1)),
      slides.length,
    ) - 1;
    const { id, label, content: Content } = slides[slideIndex];

    return (
      <StyledSlider aria-label="Content slider">
        <div
          className="sliderSlide"
          role="group"
          aria-label={ label || `Slide ${slideIndex + 1}` }
          key={ id }
        >
          <Content minEmptyHeight={ 160 } />
        </div>
      </StyledSlider>
    );
  }

  const responsive = {
    desktop: {
      breakpoint: { max: 5000, min: 1368 },
      items: itemCount(desktopItems),
    },
    tablet: {
      breakpoint: { max: 1368, min: 767 },
      items: itemCount(tabletItems),
    },
    mobile: {
      breakpoint: { max: 767, min: 0 },
      items: itemCount(mobileItems),
    },
  };
  const displayDots = showDots;

  return (
    <StyledSlider
      className={ displayDots ? 'withDots' : '' }
      style={ { '--slider-gap': `${Math.max(0, gap || 0)}px` } }
      aria-label="Content slider"
    >
      <Carousel
        arrows={ showArrows }
        autoPlay={ autoPlay }
        autoPlaySpeed={ Math.max(1000, autoPlaySpeed || 3000) }
        className="sliderCarousel"
        deviceType="desktop"
        dotListClass="sliderDots"
        draggable
        itemClass="sliderItem"
        keyBoardControl
        pauseOnHover
        renderDotsOutside={ displayDots }
        responsive={ responsive }
        shouldResetAutoplay
        showDots={ displayDots }
        slidesToSlide={ 1 }
        ssr
        swipeable
      >
        {slides.map(({ id, label, content: Content }, index) => (
          <div
            className="sliderSlide"
            role="group"
            aria-label={ label || `Slide ${index + 1}` }
            key={ id || label }
          >
            <Content minEmptyHeight={ 160 } />
          </div>
        ))}
      </Carousel>
    </StyledSlider>
  );
};

SliderComponent.propTypes = {
  slides: arrayOf(shape({
    id: string,
    label: string,
    content: func,
  })),
  editingSlide: number,
  desktopItems: number,
  tabletItems: number,
  mobileItems: number,
  gap: number,
  showArrows: bool,
  showDots: bool,
  autoPlay: bool,
  autoPlaySpeed: number,
  puck: shape({ isEditing: bool }),
};

const Slider = {
  label: 'Slider',
  fields: {
    slides: {
      type: 'array',
      label: 'Slides',
      arrayFields: {
        id: { type: 'text', visible: false },
        label: { type: 'text', label: 'Slide name' },
        content: { type: 'slot', disallow: ['Slider'] },
      },
      defaultItemProps: index => ({
        id: `slide-${Date.now()}-${index}`,
        label: `Slide ${index + 1}`,
        content: [],
      }),
      getItemSummary: (item, index) => item.label || `Slide ${(index || 0) + 1}`,
      min: 1,
    },
    editingSlide: {
      type: 'number',
      label: 'Slide to edit',
      min: 1,
    },
    desktopItems: {
      type: 'number',
      label: 'Items on desktop',
      min: 1,
      max: 6,
    },
    tabletItems: {
      type: 'number',
      label: 'Items on tablet',
      min: 1,
      max: 4,
    },
    mobileItems: {
      type: 'number',
      label: 'Items on mobile',
      min: 1,
      max: 2,
    },
    gap: {
      type: 'number',
      label: 'Gap (px)',
      min: 0,
      max: 64,
    },
    showArrows: {
      type: 'radio',
      label: 'Show arrows',
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
    showDots: {
      type: 'radio',
      label: 'Show dots',
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
    autoPlay: {
      type: 'radio',
      label: 'Auto play',
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
    autoPlaySpeed: {
      type: 'number',
      label: 'Auto play interval (ms)',
      min: 1000,
      max: 20000,
      step: 500,
    },
  },
  defaultProps: {
    slides: [
      { id: 'slide-1', label: 'Slide 1', content: [] },
      { id: 'slide-2', label: 'Slide 2', content: [] },
    ],
    editingSlide: 1,
    desktopItems: 1,
    tabletItems: 1,
    mobileItems: 1,
    gap: 20,
    showArrows: true,
    showDots: true,
    autoPlay: false,
    autoPlaySpeed: 3000,
  },
  // eslint-disable-next-line react/jsx-props-no-spreading
  render: props => <SliderComponent { ...props } />,
};

// eslint-disable-next-line import/prefer-default-export
export { Slider };
